import React, { useEffect, useState } from 'react';
import Sidebar from '../components/layout/sidebar';
import Topbar from '../components/layout/Topbar';
import { supabase } from '../supabase';
import './events.css';

const emptyForm = {
  title_en: '',
  title_ar: '',
  description_en: '',
  description_ar: '',
  image_url: '',
  event_date: '',
  event_time: '',
  location_en: '',
  location_ar: '',
  price: '',
  total_seats: '',
  available_seats: '',
  status: 'active',
};

const Events = () => {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('event_date');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('isAdminLoggedIn') !== 'true') {
      window.location.href = '/';
      return;
    }

    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);

    let query = supabase.from('events').select('*');

    if (search) {
      query = query.or(
        `title_en.ilike.%${search}%,title_ar.ilike.%${search}%,location_en.ilike.%${search}%`
      );
    }

    if (statusFilter) {
      query = query.eq('status', statusFilter);
    }

    query = query.order(sortBy, { ascending: true });

    const { data, error } = await query;

    if (error) {
      alert(error.message);
    } else {
      setEvents(data || []);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchEvents();
  }, [search, statusFilter, sortBy]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm({
      ...form,
      [name]: value,
    });
  };

  const openAddModal = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowModal(true);
  };

  const openEditModal = (event) => {
    setForm({
      title_en: event.title_en || '',
      title_ar: event.title_ar || '',
      description_en: event.description_en || '',
      description_ar: event.description_ar || '',
      image_url: event.image_url || '',
      event_date: event.event_date || '',
      event_time: event.event_time || '',
      location_en: event.location_en || '',
      location_ar: event.location_ar || '',
      price: event.price || '',
      total_seats: event.total_seats || '',
      available_seats: event.available_seats || '',
      status: event.status || 'active',
    });

    setEditingId(event.id);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...form,
      price: Number(form.price || 0),
      total_seats: Number(form.total_seats || 0),
      available_seats: Number(form.available_seats || 0),
    };

    setLoading(true);

    let result;

    if (editingId) {
      result = await supabase.from('events').update(payload).eq('id', editingId);
    } else {
      result = await supabase.from('events').insert([payload]);
    }

    setLoading(false);

    if (result.error) {
      alert(result.error.message);
      return;
    }

    setShowModal(false);
    setForm(emptyForm);
    setEditingId(null);
    fetchEvents();
  };

  const deleteEvent = async (id) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this event?');

    if (!confirmDelete) return;

    const { error } = await supabase.from('events').delete().eq('id', id);

    if (error) {
      alert(error.message);
      return;
    }

    fetchEvents();
  };

  return (
    <div className="dashboard-page">
      <Sidebar active="Events" />

      <main className="dashboard-main">
        <Topbar />

        <section className="events-content">
          <div className="events-header">
            <div>
              <h1>Events Management</h1>
              <p>Manage all literary events and activities</p>
            </div>

            <button className="add-event-btn" onClick={openAddModal}>
              + Add New Event
            </button>
          </div>

          <div className="events-filter-card">
            <div className="event-search">
              <span>⌕</span>
              <input
                placeholder="Search events..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="event_date">Sort by Date</option>
              <option value="price">Sort by Price</option>
              <option value="available_seats">Sort by Seats</option>
              <option value="title_en">Sort by Name</option>
            </select>
          </div>

          {loading && <p className="events-loading">Loading...</p>}

          <div className="events-grid">
            {events.map((event) => (
              <div className="event-card" key={event.id}>
                <div className="event-image">
                  <img
                    src={
                      event.image_url ||
                      'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f'
                    }
                    alt={event.title_en}
                  />
                  <span className={`event-status ${event.status}`}>{event.status}</span>
                </div>

                <div className="event-details">
                  <h3>{event.title_en}</h3>

                  <p>{event.event_date}</p>
                  <p>{event.location_en}</p>
                  <p>$ {event.price}</p>
                  <p>
                    {event.available_seats} / {event.total_seats} available
                  </p>

                  <div className="event-actions">
                    <button className="edit-btn" onClick={() => openEditModal(event)}>
                      ✎ Edit
                    </button>
                    <button className="delete-btn" onClick={() => deleteEvent(event.id)}>
                      🗑 Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {!loading && events.length === 0 && (
            <div className="empty-events">No events found.</div>
          )}
        </section>

        <footer className="dashboard-footer">
          <p>© 2026 Spine Literary Events Platform. All rights reserved.</p>
          <p>Admin Dashboard v1.0</p>
        </footer>
      </main>

      {showModal && (
        <div className="event-modal-overlay">
          <div className="event-modal">
            <div className="modal-header">
              <h2>{editingId ? 'Edit Event' : 'Add New Event'}</h2>
              <button onClick={() => setShowModal(false)}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="event-form">
              <div className="form-grid">
                <input
                  name="title_en"
                  placeholder="Title EN"
                  value={form.title_en}
                  onChange={handleChange}
                  required
                />

                <input
                  name="title_ar"
                  placeholder="Title AR"
                  value={form.title_ar}
                  onChange={handleChange}
                  required
                />

                <input
                  name="location_en"
                  placeholder="Location EN"
                  value={form.location_en}
                  onChange={handleChange}
                />

                <input
                  name="location_ar"
                  placeholder="Location AR"
                  value={form.location_ar}
                  onChange={handleChange}
                />

                <input
                  name="event_date"
                  type="date"
                  value={form.event_date}
                  onChange={handleChange}
                  required
                />

                <input
                  name="event_time"
                  type="time"
                  value={form.event_time}
                  onChange={handleChange}
                />

                <input
                  name="price"
                  type="number"
                  placeholder="Price"
                  value={form.price}
                  onChange={handleChange}
                />

                <input
                  name="total_seats"
                  type="number"
                  placeholder="Total Seats"
                  value={form.total_seats}
                  onChange={handleChange}
                />

                <input
                  name="available_seats"
                  type="number"
                  placeholder="Available Seats"
                  value={form.available_seats}
                  onChange={handleChange}
                />

                <select name="status" value={form.status} onChange={handleChange}>
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="cancelled">Cancelled</option>
                </select>

                <input
                  className="full"
                  name="image_url"
                  placeholder="Image URL"
                  value={form.image_url}
                  onChange={handleChange}
                />

                <textarea
                  className="full"
                  name="description_en"
                  placeholder="Description EN"
                  value={form.description_en}
                  onChange={handleChange}
                />

                <textarea
                  className="full"
                  name="description_ar"
                  placeholder="Description AR"
                  value={form.description_ar}
                  onChange={handleChange}
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit">
                  {editingId ? 'Save Changes' : 'Create Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;